import React, { useEffect, useState } from "react";
import CommonModal from "./CommonModal";
import loadingMojo from "@src/assets/images/loadingMojo.gif";
import { logEvent } from "@src/utils/logClient";
import { useCreateConvertExperimentEvent } from "@src/utils/experiment";

const HUBSPOT_SCRIPT_URL = "https://js-na2.hsforms.net/forms/embed/developer/243899479.js";
const HUBSPOT_FORM_ID = "2224a87d-1653-4478-ba8b-b9cdd93b3b53";
const HUBSPOT_PORTAL_ID = "243899479";

type BookACallModalProps = {
  closeModal: () => void;
  logEventContext: string;
  experimentName?: string;
};

/**
 * Modal for the "Book a Call" HubSpot form.
 * Replaces the previous Calendly-based modal with an embedded HubSpot form.
 */
const BookACallModal: React.FC<BookACallModalProps> = ({ closeModal, logEventContext, experimentName }) => {
  const [isLoading, setIsLoading] = useState(true);
  const convertExperiment = useCreateConvertExperimentEvent(experimentName || "");

  // Log modal open event
  useEffect(() => {
    logEvent({
      eventName: `web.external.${logEventContext}.book_a_call_modal_opened`,
      eventValue: window.location.href,
      ...(experimentName && { experiments: [experimentName] }),
    });
  }, []);

  // Load HubSpot form script.
  // The HubSpot embed script only scans for .hs-form-html containers on initial
  // execution. Since React unmounts the container when the modal closes, we must
  // remove and re-add the script each time to force HubSpot to re-initialize.
  useEffect(() => {
    const existingScript = document.querySelector(`script[src="${HUBSPOT_SCRIPT_URL}"]`);
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");
    script.src = HUBSPOT_SCRIPT_URL;
    script.defer = true;
    script.onload = () => {
      setTimeout(() => setIsLoading(false), 500);
    };
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  // Listen for HubSpot form submission via postMessage.
  // The newer HubSpot embed API (hs-form-html / developer script) sends
  // { formGuid, accepted: true } on successful submission, rather than
  // the legacy hsFormCallback format.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.formGuid === HUBSPOT_FORM_ID && event.data?.accepted === true) {
        logEvent({
          eventName: `web.external.${logEventContext}.hubspot_form_submitted`,
          eventValue: window.location.href,
          ...(experimentName && { experiments: [experimentName] }),
        });

        if (experimentName) {
          convertExperiment();
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [logEventContext, experimentName]);

  return (
    <CommonModal fullPage noHeader closeModal={closeModal}>
      <div className="w-full max-w-[600px] mx-auto p-lg text-center">
        <div className="min-h-[400px] text-left">
          {isLoading && (
            <div className="flex items-center justify-center min-h-[400px]">
              <img src={loadingMojo} alt="Loading..." className="w-[100px] h-[100px]" />
            </div>
          )}
          <div
            className={`hs-form-html ${isLoading ? "hidden" : ""}`}
            data-region="na2"
            data-form-id={HUBSPOT_FORM_ID}
            data-portal-id={HUBSPOT_PORTAL_ID}
          />
        </div>
      </div>
    </CommonModal>
  );
};

export default BookACallModal;
